import React, { useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

import { useTranslation } from 'hooks/useTranslation';
import { Loaders } from 'shared';

import { useStyles } from './styles';

import { useTopUpBalanceByCreditCardSummary } from 'features/finance/hooks/useTopUpBalanceByCreditCardSummary';
import { getFormattedAmountWithCurrency } from 'features/finance/utils/get-formatted-amount-with-currency';

type Props = {
  amount: number;
  currency: string;
};

export const TopUpSummary: React.FC<Props> = ({ amount, currency }) => {
  const { t } = useTranslation('finance');

  const classes = useStyles();

  const { data, error, isLoading } = useTopUpBalanceByCreditCardSummary(amount, currency);

  const topUpAmountTitle = useMemo(
    () => (data ? getFormattedAmountWithCurrency(data.top_up_amount, data.currency) : ''),
    [data]
  );
  const topUpProcessingFeeTitle = useMemo(
    () => (data ? getFormattedAmountWithCurrency(data.processing_fee, data.currency) : ''),
    [data]
  );
  const topUpVatProcessingFee = useMemo(
    () => (data ? getFormattedAmountWithCurrency(data.vat_processing_fee, data.currency) : ''),
    [data]
  );
  const topUpTotalAmountTitle = useMemo(
    () => (data ? getFormattedAmountWithCurrency(data.total_amount, data.currency) : ''),
    [data]
  );

  return (
    <Box className={classes.topUpSummary}>
      {isLoading ? (
        <Loaders.Loader />
      ) : error ? (
        <Box display="flex" justifyContent="center">
          <Typography color="error">{`${error}`}</Typography>
        </Box>
      ) : (
        <>
          <Box className={classes.topUpSummaryHeader}>
            <Typography className={classes.topUpSummaryTitle}>{t('topUpSummary.title')}</Typography>
          </Box>
          <Box className={classes.topUpSummaryMain}>
            <Box paddingBottom={10} display="flex" justifyContent="space-between">
              <Typography>{t('topUpSummary.amount')}</Typography>
              <Typography> {topUpAmountTitle}</Typography>
            </Box>
            <Box paddingBottom={10.25} display="flex" justifyContent="space-between">
              <Typography>{t('topUpSummary.processingFee')}</Typography>
              <Typography>{topUpProcessingFeeTitle}</Typography>
            </Box>
            <Box paddingBottom={10.25} display="flex" justifyContent="space-between">
              <Typography>{t('topUpSummary.vatProcessingFee')}</Typography>
              <Typography>{topUpVatProcessingFee}</Typography>
            </Box>
            <Divider />
            <Box paddingTop={11.25} display="flex" justifyContent="space-between">
              <Typography>{t('topUpSummary.totalAmountToBePaid')}</Typography>
              <Typography>{topUpTotalAmountTitle}</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
